import { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	Button,
	TextField,
	IconButton,
	useMediaQuery,
	useTheme
} from '@mui/material'
import { sfTint, sfInitials, statusViz } from '../utils/employeeDisplay'

// Nami lavender theme tokens (from the "Staff Management" design handoff)
const FONT = "'Outfit', sans-serif"
const EMP = {
	page: '#FBFAFE',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	danger: '#BE4B45',
	dangerSoft: '#FBECEB'
}

const EMPTY_FORM = {
	first_name: '',
	last_name: '',
	email: '',
	phone: '',
	address: '',
	services: [],
	active: true
}

// ── shared icon set (matches the SfIc glyphs from the design) ──
function EmpIc({ name, size = 16, sw = 1.6, color = 'currentColor' }) {
	const P = {
		search: <><circle cx="7" cy="7" r="4.6" /><path d="M10.4 10.4L14 14" /></>,
		plus: <path d="M8 3v10M3 8h10" />,
		back: <path d="M10 3.5L5.5 8l4.5 4.5" />,
		trash: <path d="M3 4.5h10M6.5 4V3h3v1M5 4.5l.5 8h5l.5-8" />,
		check: <path d="M3 8.5l3.5 3.5L13 4.5" />,
		phone: <path d="M3 4.5a1.5 1.5 0 011.5-1.5h1.7l1 2.5-1.3 1a8 8 0 003.6 3.6l1-1.3 2.5 1v1.7A1.5 1.5 0 0112.5 13 9.5 9.5 0 013 4.5z" />,
		mail: <><rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2.5 4.5L8 8.5l5.5-4" /></>,
		pin: <><path d="M8 14.5s4.7-4.2 4.7-7.6A4.7 4.7 0 008 2.2a4.7 4.7 0 00-4.7 4.7C3.3 10.3 8 14.5 8 14.5z" /><circle cx="8" cy="6.9" r="1.7" /></>
	}
	return (
		<svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color}
			strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
			{P[name] || null}
		</svg>
	)
}

// ── labelled text field styled like the design's .sf-input / .smb-input ──
function EmpField({ label, value, onChange, placeholder, type = 'text', required = false, multiline = false, height = 40 }) {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
			<Typography component="span" sx={{ fontFamily: FONT, fontSize: '12px', fontWeight: 650, color: EMP.text }}>
				{label}
				{required && <Box component="span" sx={{ color: EMP.danger, ml: '2px' }}>*</Box>}
			</Typography>
			<TextField
				fullWidth
				type={type}
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				multiline={multiline}
				minRows={multiline ? 3 : undefined}
				variant="outlined"
				sx={{
					'& .MuiOutlinedInput-root': {
						bgcolor: EMP.field,
						borderRadius: '8px',
						fontFamily: FONT,
						fontSize: '13.5px',
						color: EMP.text,
						'& fieldset': { borderColor: EMP.border, borderWidth: '1.5px' },
						'&:hover fieldset': { borderColor: EMP.primary },
						'&.Mui-focused fieldset': { borderColor: EMP.primary, borderWidth: '1.5px' }
					},
					'& input.MuiOutlinedInput-input': { height: `${height}px`, py: 0, boxSizing: 'border-box' },
					'& .MuiOutlinedInput-input': {
						fontFamily: FONT,
						fontSize: '13.5px',
						color: EMP.text,
						'&::placeholder': { color: EMP.muted, opacity: 1 }
					}
				}}
			/>
		</Box>
	)
}

// ── selectable service card (design's .smb-svc / desktop services grid) ──
function ServiceCard({ name, on, onToggle }) {
	return (
		<Box
			component="button"
			type="button"
			onClick={onToggle}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '11px',
				textAlign: 'left',
				width: '100%',
				minWidth: 0,
				cursor: 'pointer',
				padding: '10px 12px',
				borderRadius: '11px',
				fontFamily: FONT,
				bgcolor: on ? '#F7F5FC' : EMP.field,
				border: on ? '1.5px solid #D8CEF2' : '1.5px solid #EAE6F3',
				'&:hover': { borderColor: EMP.primary }
			}}
		>
			<Box sx={{
				width: 20,
				height: 20,
				flex: 'none',
				borderRadius: '6px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				bgcolor: on ? EMP.primary : EMP.field,
				border: on ? `1.5px solid ${EMP.primary}` : '1.5px solid #D4CDE6',
				color: '#FFFFFF'
			}}>
				{on && <EmpIc name="check" size={12} sw={2.4} />}
			</Box>
			<Typography component="span" sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: EMP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				{name}
			</Typography>
		</Box>
	)
}

function StatusPill({ active, onToggle }) {
	const sv = statusViz(active)
	return (
		<Box
			component="button"
			type="button"
			onClick={onToggle}
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '7px',
				height: '28px',
				padding: '0 12px 0 10px',
				border: 'none',
				borderRadius: '99px',
				cursor: 'pointer',
				fontFamily: FONT,
				fontSize: '12.5px',
				fontWeight: 700,
				bgcolor: sv.bg,
				color: sv.fg
			}}
		>
			<Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: sv.dot }} />
			{sv.label}
		</Box>
	)
}

function AddEditEmployeePage({ employee, services = [], onSubmit, onDelete, onClose, loading = false, error = '', isMobile: isMobileProp }) {
	const theme = useTheme()
	const isSmall = useMediaQuery(theme.breakpoints.down('md'))
	const isMobile = isMobileProp !== undefined ? isMobileProp : isSmall

	const [tab, setTab] = useState('profile')
	const [localError, setLocalError] = useState('')
	const [formData, setFormData] = useState(EMPTY_FORM)

	useEffect(() => {
		if (employee) {
			setFormData({
				first_name: employee.first_name || '',
				last_name: employee.last_name || '',
				email: employee.email || '',
				phone: employee.phone || '',
				address: employee.address || '',
				services: employee.services || [],
				active: employee.active !== false
			})
		} else {
			setFormData(EMPTY_FORM)
		}
		setTab('profile')
		setLocalError('')
	}, [employee])

	const isEdit = !!employee
	const first = formData.first_name.trim() || (isEdit ? 'staff' : 'staff member')
	const fullName = `${formData.first_name} ${formData.last_name}`.trim() || 'New staff member'
	const tint = sfTint(fullName)

	const handleChange = (field) => (event) => {
		setFormData((prev) => ({ ...prev, [field]: event.target.value }))
	}

	const toggleService = (serviceId) => {
		setFormData((prev) => ({
			...prev,
			services: prev.services.includes(serviceId)
				? prev.services.filter((id) => id !== serviceId)
				: [...prev.services, serviceId]
		}))
	}

	const allSelected = services.length > 0 && services.every((s) => formData.services.includes(s.id))
	const toggleAllServices = () => {
		setFormData((prev) => ({ ...prev, services: allSelected ? [] : services.map((s) => s.id) }))
	}

	const toggleStatus = () => setFormData((prev) => ({ ...prev, active: !prev.active }))

	const handleSave = () => {
		if (!formData.first_name.trim()) {
			setLocalError('First name is required')
			setTab('profile')
			return
		}
		if (!formData.last_name.trim()) {
			setLocalError('Last name is required')
			setTab('profile')
			return
		}
		setLocalError('')
		onSubmit({
			first_name: formData.first_name.trim(),
			last_name: formData.last_name.trim(),
			email: formData.email.trim(),
			phone: formData.phone.trim(),
			address: formData.address.trim(),
			services: formData.services,
			active: formData.active
		})
	}

	const shownError = localError || error

	// ── Profile tab body ──
	const profileTab = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '14px' : 0 }}>
			<Box sx={{ bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '16px', padding: isMobile ? '16px 16px 18px' : '24px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)' }}>
				<Typography sx={{ fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: EMP.primaryDeep, mb: '16px' }}>
					Personal details
				</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
					<EmpField label="First name" required value={formData.first_name} onChange={handleChange('first_name')} placeholder="First name" />
					<EmpField label="Last name" required value={formData.last_name} onChange={handleChange('last_name')} placeholder="Last name" />
					<EmpField label="Email" type="email" value={formData.email} onChange={handleChange('email')} placeholder="name@example.com" />
					<EmpField label="Phone" value={formData.phone} onChange={handleChange('phone')} placeholder="(555) 123-4567" />
				</Box>

				<Box sx={{ height: '1px', bgcolor: '#EFEBF7', my: '22px' }} />

				<Typography sx={{ fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: EMP.primaryDeep, mb: '16px' }}>
					Address
				</Typography>
				<EmpField label="Address" value={formData.address} onChange={handleChange('address')} placeholder="Street address, city, state, zip code" multiline />
			</Box>
		</Box>
	)

	// ── Services tab body ──
	const servicesInner = (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '0 0 4px' : '16px 20px', borderBottom: isMobile ? 'none' : '1px solid #EFEBF7' }}>
				<Box sx={{ minWidth: 0 }}>
					{isMobile ? (
						<>
							<Typography sx={{ fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: EMP.primaryDeep }}>
								Bookable with {first}
							</Typography>
							<Typography sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 600, color: EMP.muted, mt: '4px' }}>
								Only checked services can be booked · empty means all services
							</Typography>
						</>
					) : (
						<>
							<Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 750, color: EMP.text }}>
								Services this staff member can perform
							</Typography>
							<Typography sx={{ fontFamily: FONT, fontSize: '12.5px', fontWeight: 600, color: EMP.muted, mt: '1px' }}>
								Only checked services can be booked · leave empty for all services
							</Typography>
						</>
					)}
				</Box>
				<Box sx={{ flex: 1 }} />
				{services.length > 0 && (
					<Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px', bgcolor: EMP.primarySoft, color: EMP.primaryDeep, borderRadius: '99px', padding: '5px 12px', fontFamily: FONT, fontSize: '12.5px', fontWeight: 750, whiteSpace: 'nowrap' }}>
						<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800 }}>{formData.services.length}</Box>of {services.length}
					</Box>
				)}
			</Box>

			<Box sx={{ padding: isMobile ? '12px 0 0' : '6px 20px 18px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '9px' }}>
					<Typography component="span" sx={{ fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: EMP.muted }}>
						Services
					</Typography>
					<Box sx={{ flex: 1, height: '1px', bgcolor: '#F1EEF8' }} />
					{services.length > 0 && (
						<Box component="button" type="button" onClick={toggleAllServices} sx={{ border: 'none', background: 'transparent', color: EMP.primary, fontFamily: FONT, fontSize: '12px', fontWeight: 700, cursor: 'pointer', '&:hover': { color: EMP.primaryDeep } }}>
							{allSelected ? 'Clear all' : 'Select all'}
						</Box>
					)}
				</Box>
				{services.length === 0 ? (
					<Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: EMP.muted, py: '12px' }}>
						No services available. Please add services first.
					</Typography>
				) : (
					<Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
						{services.map((s) => (
							<ServiceCard key={s.id} name={s.name} on={formData.services.includes(s.id)} onToggle={() => toggleService(s.id)} />
						))}
					</Box>
				)}
			</Box>
		</>
	)

	const servicesTab = isMobile ? (
		<Box sx={{ bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '16px', padding: '16px 16px 18px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)' }}>
			{servicesInner}
		</Box>
	) : (
		<Box sx={{ bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '14px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)', overflow: 'hidden' }}>
			{servicesInner}
		</Box>
	)

	const tabBar = (
		<Box sx={{ display: 'inline-flex', gap: '3px', bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '11px', padding: '4px', mb: '16px' }}>
			{[['profile', 'Profile'], ['services', 'Services']].map(([id, label]) => (
				<Box
					key={id}
					component="button"
					type="button"
					onClick={() => setTab(id)}
					sx={{
						border: 'none',
						borderRadius: '8px',
						padding: '8px 20px',
						fontFamily: FONT,
						fontSize: '13.5px',
						cursor: 'pointer',
						fontWeight: tab === id ? 750 : 650,
						bgcolor: tab === id ? EMP.primarySoft : 'transparent',
						color: tab === id ? EMP.primaryDeep : EMP.muted
					}}
				>
					{label}
				</Box>
			))}
		</Box>
	)

	// ═══════════════ MOBILE ═══════════════
	if (isMobile) {
		return (
			<Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', flexDirection: 'column', bgcolor: EMP.field }}>
				{/* Header */}
				<Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 16px', bgcolor: EMP.field, borderBottom: `1px solid ${EMP.border}` }}>
					<IconButton onClick={onClose} aria-label="Back" sx={{ width: 40, height: 40, ml: '-8px', borderRadius: '11px', color: EMP.text, '&:hover': { bgcolor: EMP.surface } }}>
						<EmpIc name="back" size={17} sw={1.8} />
					</IconButton>
					<Typography sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em', color: EMP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{isEdit ? `Edit ${first}` : 'New staff member'}
					</Typography>
					<Button
						onClick={handleSave}
						disabled={loading}
						startIcon={<EmpIc name="check" size={13} sw={2.2} />}
						sx={{ height: '38px', px: '15px', border: 'none', borderRadius: '11px', bgcolor: EMP.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '13.5px', boxShadow: '0 2px 8px rgba(124,107,217,0.28)', '&:hover': { bgcolor: EMP.primaryDeep }, '&:disabled': { bgcolor: '#CBBEF0', color: '#FFFFFF', boxShadow: 'none' }, '& .MuiButton-startIcon': { marginRight: '6px' } }}
					>
						Save
					</Button>
				</Box>

				{/* Body */}
				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px', padding: '16px 16px 24px' }}>
					{shownError && (
						<Box sx={{ bgcolor: EMP.dangerSoft, color: EMP.danger, fontFamily: FONT, fontSize: '13px', fontWeight: 600, borderRadius: '12px', padding: '10px 14px' }}>
							{shownError}
						</Box>
					)}

					{/* Identity */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
						<Box sx={{ width: 72, height: 72, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontSize: '23px', fontWeight: 800, bgcolor: tint.bg, color: tint.fg }}>
							{sfInitials(fullName)}
						</Box>
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
							<Typography sx={{ fontFamily: FONT, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em', color: EMP.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
								{fullName}
							</Typography>
							<StatusPill active={formData.active} onToggle={toggleStatus} />
						</Box>
					</Box>

					{/* Tabs */}
					<Box role="tablist" sx={{ display: 'flex', gap: '3px', bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '12px', padding: '4px', boxShadow: '0 4px 14px rgba(46,42,64,0.05)' }}>
						{[['profile', 'Profile'], ['services', 'Services']].map(([id, label]) => (
							<Box
								key={id}
								component="button"
								type="button"
								role="tab"
								aria-selected={tab === id}
								onClick={() => setTab(id)}
								sx={{ flex: 1, border: 'none', borderRadius: '9px', padding: '9px 0', bgcolor: tab === id ? EMP.primarySoft : 'transparent', fontFamily: FONT, fontSize: '13.5px', fontWeight: tab === id ? 750 : 650, color: tab === id ? EMP.primaryDeep : EMP.muted, cursor: 'pointer' }}
							>
								{label}
							</Box>
						))}
					</Box>

					{tab === 'profile' ? profileTab : servicesTab}

					{isEdit && onDelete && (
						<Box
							component="button"
							type="button"
							onClick={() => onDelete(employee)}
							sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: '44px', border: 'none', borderRadius: '12px', background: 'transparent', color: EMP.danger, fontFamily: FONT, fontSize: '13.5px', fontWeight: 650, cursor: 'pointer', '&:hover': { bgcolor: EMP.dangerSoft } }}
						>
							<EmpIc name="trash" size={15} />Remove staff member
						</Box>
					)}
				</Box>
			</Box>
		)
	}

	// ═══════════════ DESKTOP ═══════════════
	return (
		<Box sx={{ padding: '22px 26px 30px', bgcolor: EMP.page, minHeight: '100%', boxSizing: 'border-box' }}>
			{/* Page head */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mb: '18px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<IconButton onClick={onClose} aria-label="Back to staff" sx={{ width: 34, height: 34, border: `1px solid ${EMP.border}`, borderRadius: '9px', bgcolor: EMP.field, color: EMP.muted, '&:hover': { color: EMP.primaryDeep, borderColor: EMP.primary } }}>
						<EmpIc name="back" size={16} sw={1.8} />
					</IconButton>
					<Typography sx={{ fontFamily: FONT, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em', color: EMP.text }}>
						{isEdit ? `Edit ${first}` : 'New staff member'}
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Button onClick={onClose} sx={{ height: '40px', px: '16px', border: `1.5px solid ${EMP.border}`, borderRadius: '10px', bgcolor: EMP.field, color: EMP.text, textTransform: 'none', fontFamily: FONT, fontWeight: 650, fontSize: '13.5px', '&:hover': { borderColor: EMP.primary, color: EMP.primaryDeep, bgcolor: EMP.field } }}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={loading} startIcon={<EmpIc name="check" size={15} sw={2} />} sx={{ height: '40px', px: '16px', border: 'none', borderRadius: '10px', bgcolor: EMP.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '13.5px', boxShadow: '0 2px 8px rgba(124,107,217,0.28)', '&:hover': { bgcolor: EMP.primaryDeep }, '&:disabled': { bgcolor: '#CBBEF0', color: '#FFFFFF', boxShadow: 'none' }, '& .MuiButton-startIcon': { marginRight: '7px' } }}>
						{loading ? 'Saving...' : 'Save changes'}
					</Button>
				</Box>
			</Box>

			{shownError && (
				<Box sx={{ bgcolor: EMP.dangerSoft, color: EMP.danger, fontFamily: FONT, fontSize: '13px', fontWeight: 600, borderRadius: '10px', padding: '10px 14px', mb: '16px' }}>
					{shownError}
				</Box>
			)}

			<Box sx={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
				{/* Identity panel */}
				<Box component="aside" sx={{ flex: 'none', width: '312px', bgcolor: EMP.field, border: `1px solid ${EMP.border}`, borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
						<Box sx={{ width: 96, height: 96, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontSize: '30px', fontWeight: 800, bgcolor: tint.bg, color: tint.fg }}>
							{sfInitials(fullName)}
						</Box>
						<Box>
							<Typography sx={{ fontFamily: FONT, fontSize: '18px', fontWeight: 800, letterSpacing: '-0.015em', color: EMP.text }}>
								{fullName}
							</Typography>
						</Box>
						<StatusPill active={formData.active} onToggle={toggleStatus} />
					</Box>

					{(formData.phone || formData.email || formData.address) && (
						<Box>
							<Typography sx={{ fontFamily: FONT, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: EMP.primaryDeep, mb: '11px' }}>
								Contact
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
								{formData.phone && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontFamily: FONT, fontSize: '13px', fontWeight: 550, color: '#3A3650' }}>
										<Box sx={{ mt: '1px', flex: 'none' }}><EmpIc name="phone" size={15} sw={1.5} color={EMP.primary} /></Box>{formData.phone}
									</Box>
								)}
								{formData.email && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontFamily: FONT, fontSize: '13px', fontWeight: 550, color: '#3A3650', overflowWrap: 'anywhere' }}>
										<Box sx={{ mt: '1px', flex: 'none' }}><EmpIc name="mail" size={15} sw={1.5} color={EMP.primary} /></Box>{formData.email}
									</Box>
								)}
								{formData.address && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontFamily: FONT, fontSize: '13px', fontWeight: 550, color: '#3A3650' }}>
										<Box sx={{ mt: '1px', flex: 'none' }}><EmpIc name="pin" size={15} sw={1.5} color={EMP.primary} /></Box>{formData.address}
									</Box>
								)}
							</Box>
						</Box>
					)}

					{isEdit && onDelete && (
						<Box
							component="button"
							type="button"
							onClick={() => onDelete(employee)}
							sx={{ mt: '2px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: '38px', border: 'none', borderRadius: '10px', background: 'transparent', color: EMP.danger, fontFamily: FONT, fontSize: '13px', fontWeight: 650, cursor: 'pointer', '&:hover': { bgcolor: EMP.dangerSoft } }}
						>
							<EmpIc name="trash" size={15} />Remove staff member
						</Box>
					)}
				</Box>

				{/* Tabbed main */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					{tabBar}
					{tab === 'profile' ? profileTab : servicesTab}
				</Box>
			</Box>
		</Box>
	)
}

export default AddEditEmployeePage
